/*
 * FROSTBEULE – 3D game box interaction (owner: CORE). Custom element <fb-box-3d> from snippets/frost-box-3d.
 * spin="float": idle float (CSS) + pointer tilt on mouse hover + drag/swipe to spin, spring return to the front.
 * spin="turn": CSS turntable only. spin="static": nothing to do.
 * Writes --tx / --ty (deg) and --tn (−1…1, gloss) on the element; only while a spring is moving (rAF).
 * Motion soft/none: no tilt, no drag (pose stays). Safe to load more than once (guarded).
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('fb-box-3d')) return;

  var root = document.documentElement;
  var raf =
    window.requestAnimationFrame ||
    function (fn) {
      return window.setTimeout(function () {
        fn(Date.now());
      }, 16);
    };
  var caf = window.cancelAnimationFrame || window.clearTimeout;

  function motion() {
    return root.getAttribute('data-motion') || 'full';
  }

  function now() {
    return window.performance ? performance.now() : Date.now();
  }

  /* fallback spring if frost-global.js is missing (same API as FROSTBEULE.spring) */
  function makeSpring(opts) {
    if (window.FROSTBEULE && typeof window.FROSTBEULE.spring === 'function') return window.FROSTBEULE.spring(opts);
    var s = { value: 0, velocity: 0, target: 0 };
    var frame = null;
    var last = 0;
    function step(t) {
      var dt = Math.min(0.064, (t - last) / 1000 || 0.016);
      last = t;
      var sub = Math.max(1, Math.ceil(dt * 120));
      var h = dt / sub;
      for (var i = 0; i < sub; i += 1) {
        s.velocity += (-opts.stiffness * (s.value - s.target) - opts.damping * s.velocity) * h;
        s.value += s.velocity * h;
      }
      if (Math.abs(s.velocity) < 0.001 && Math.abs(s.value - s.target) < 0.001) {
        s.value = s.target;
        s.velocity = 0;
        frame = null;
        opts.onUpdate(s.value);
        return;
      }
      opts.onUpdate(s.value);
      frame = raf(step);
    }
    s.set = function (target) {
      s.target = target;
      if (!frame) {
        last = now();
        frame = raf(step);
      }
    };
    s.jump = function (v) {
      s.value = v;
      s.target = v;
      s.velocity = 0;
      opts.onUpdate(v);
    };
    s.stop = function () {
      if (frame) caf(frame);
      frame = null;
    };
    return s;
  }

  var HOVER_Y = 14; /* deg yaw at the element edge */
  var HOVER_X = 8; /* deg pitch at the element edge */
  var DRAG_DEG_PER_PX = 0.55;

  class FbBox3d extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.mode = this.getAttribute('data-spin') || 'float';
      if (this.mode !== 'float') return;
      this._bound = true;

      var self = this;
      this.tx = 0;
      this.ty = 0;
      this.drag = null;
      this.applyFrame = null;
      this.springX = makeSpring({
        stiffness: 140,
        damping: 13,
        onUpdate: function (v) {
          self.tx = v;
          self.schedule();
        },
      });
      this.springY = makeSpring({
        stiffness: 120,
        damping: 11,
        onUpdate: function (v) {
          self.ty = v;
          self.schedule();
        },
      });

      this.onEnterMove = this.onEnterMove.bind(this);
      this.onLeave = this.onLeave.bind(this);
      this.onDown = this.onDown.bind(this);
      this.onMove = this.onMove.bind(this);
      this.onUp = this.onUp.bind(this);
      this.onMotion = this.onMotion.bind(this);

      this.addEventListener('pointermove', this.onEnterMove);
      this.addEventListener('pointerleave', this.onLeave);
      this.addEventListener('pointerdown', this.onDown);
      this.addEventListener('dragstart', function (e) {
        e.preventDefault();
      });
      document.addEventListener('frost:motion-change', this.onMotion);
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      if (!this._bound) return;
      this._bound = false;
      this.removeEventListener('pointermove', this.onEnterMove);
      this.removeEventListener('pointerleave', this.onLeave);
      this.removeEventListener('pointerdown', this.onDown);
      window.removeEventListener('pointermove', this.onMove);
      window.removeEventListener('pointerup', this.onUp);
      window.removeEventListener('pointercancel', this.onUp);
      document.removeEventListener('frost:motion-change', this.onMotion);
      if (this.springX) this.springX.stop();
      if (this.springY) this.springY.stop();
      if (this.applyFrame) caf(this.applyFrame);
    }

    active() {
      return motion() === 'full';
    }

    schedule() {
      if (this.applyFrame) return;
      var self = this;
      this.applyFrame = raf(function () {
        self.applyFrame = null;
        self.style.setProperty('--tx', self.tx.toFixed(2) + 'deg');
        self.style.setProperty('--ty', self.ty.toFixed(2) + 'deg');
        self.style.setProperty('--tn', Math.max(-1, Math.min(1, self.ty / 40)).toFixed(3));
      });
    }

    onMotion() {
      this.drag = null;
      this.classList.remove('is-dragging');
      this.springX.jump(0);
      this.springY.jump(0);
    }

    /* hover tilt: mouse / pen only, never while dragging */
    onEnterMove(e) {
      if (this.drag || e.pointerType === 'touch' || !this.active()) return;
      var r = this.getBoundingClientRect();
      if (!r.width || !r.height) return;
      var nx = Math.max(-1, Math.min(1, ((e.clientX - r.left) / r.width) * 2 - 1));
      var ny = Math.max(-1, Math.min(1, ((e.clientY - r.top) / r.height) * 2 - 1));
      this.springY.set(nx * HOVER_Y);
      this.springX.set(-ny * HOVER_X);
    }

    onLeave(e) {
      if (this.drag && this.drag.active) return;
      if (e && e.pointerType === 'touch') return;
      this.springX.set(0);
      this.springY.set(0);
    }

    onDown(e) {
      /* In the product gallery a touch swipe belongs to the gallery (it pages), never to the box. */
      if (e.pointerType === 'touch' && this.closest('.fb-gallery')) return;
      if (!this.active()) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.drag = {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        startY: this.ty,
        startX: this.tx,
        active: false,
        lastX: e.clientX,
        lastT: now(),
        v: 0,
      };
      window.addEventListener('pointermove', this.onMove, { passive: true });
      window.addEventListener('pointerup', this.onUp);
      window.addEventListener('pointercancel', this.onUp);
    }

    onMove(e) {
      var d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      var dx = e.clientX - d.x;
      var dy = e.clientY - d.y;
      if (!d.active) {
        /* horizontal intent only – vertical swipes keep scrolling the page */
        if (Math.abs(dx) < 6 || Math.abs(dx) < Math.abs(dy)) return;
        d.active = true;
        d.x = e.clientX;
        dx = 0;
        this.classList.add('is-dragging');
        try {
          this.setPointerCapture(d.id);
        } catch (err) {}
        this.springX.stop();
        this.springY.stop();
      }
      var t = now();
      var dt = Math.max(1, t - d.lastT);
      d.v = ((e.clientX - d.lastX) * DRAG_DEG_PER_PX * 1000) / dt;
      d.lastX = e.clientX;
      d.lastT = t;
      this.springY.jump(d.startY + dx * DRAG_DEG_PER_PX);
      this.springX.jump(Math.max(-10, Math.min(10, d.startX - dy * 0.08)));
    }

    onUp(e) {
      var d = this.drag;
      if (!d || (e && e.pointerId !== d.id)) return;
      window.removeEventListener('pointermove', this.onMove);
      window.removeEventListener('pointerup', this.onUp);
      window.removeEventListener('pointercancel', this.onUp);
      this.drag = null;
      if (!d.active) return;
      this.classList.remove('is-dragging');
      try {
        this.releasePointerCapture(d.id);
      } catch (err) {}
      /* fling: keep the release speed, then spring back to the front the shortest way round
         (normalising by full turns is invisible, the pose is identical) */
      var y = ((((this.ty + 180) % 360) + 360) % 360) - 180;
      this.springY.jump(y);
      this.springY.velocity = Math.max(-700, Math.min(700, d.v * 0.5));
      this.springY.set(0);
      this.springX.set(0);
    }
  }

  window.customElements.define('fb-box-3d', FbBox3d);
})();
